/**
 * Keeps JSON Schema `required` in sync with `properties` after Formily/designer
 * reorder or renames. Stale entries cause Formily to validate missing paths.
 *
 * - Maps `required` entries that match a field's `name` (or `x-data.name`) to property keys.
 * - Drops entries that do not match any child field.
 * - Removes `required` on `void` layout nodes (children carry their own rules).
 * - Removes `required` when there is no `properties` object.
 */

type SchemaNode = Record<string, unknown>;

const fieldApiName = (def: unknown): string | undefined => {
  if (!def || typeof def !== "object" || Array.isArray(def)) {
    return undefined;
  }
  const d = def as SchemaNode;
  if (typeof d.name === "string" && d.name.length > 0) {
    return d.name;
  }
  const xData = d["x-data"];
  if (xData && typeof xData === "object" && !Array.isArray(xData)) {
    const n = (xData as SchemaNode).name;
    if (typeof n === "string" && n.length > 0) {
      return n;
    }
  }
  return undefined;
};

const buildNameToPropertyKey = (
  properties: Record<string, unknown>
): Map<string, string> => {
  const map = new Map<string, string>();
  for (const propKey of Object.keys(properties)) {
    map.set(propKey, propKey);
    const api = fieldApiName(properties[propKey]);
    if (api) {
      map.set(api, propKey);
    }
  }
  return map;
};

const resolveRequiredKeys = (
  required: unknown,
  properties: Record<string, unknown>
): string[] => {
  if (!Array.isArray(required) || required.length === 0) {
    return [];
  }
  const nameToKey = buildNameToPropertyKey(properties);
  const propKeys = new Set(Object.keys(properties));
  const out = new Set<string>();

  for (const entry of required) {
    if (typeof entry !== "string") {
      continue;
    }
    if (propKeys.has(entry)) {
      out.add(entry);
      continue;
    }
    const resolved = nameToKey.get(entry);
    if (resolved && propKeys.has(resolved)) {
      out.add(resolved);
    }
  }
  return [...out];
};

export const sanitizeJsonSchemaRequired = (node: unknown): unknown => {
  if (!node || typeof node !== "object") {
    return node;
  }
  if (Array.isArray(node)) {
    return node.map(sanitizeJsonSchemaRequired);
  }

  const obj = node as SchemaNode;
  const clone: SchemaNode = { ...obj };

  if (clone.type === "void" && Array.isArray(clone.required)) {
    delete clone.required;
  }

  if (
    clone.properties &&
    typeof clone.properties === "object" &&
    !Array.isArray(clone.properties)
  ) {
    const props = clone.properties as Record<string, unknown>;
    const cleanedProps = Object.fromEntries(
      Object.entries(props).map(([k, v]) => [k, sanitizeJsonSchemaRequired(v)])
    );
    clone.properties = cleanedProps;
    if (Array.isArray(clone.required)) {
      clone.required = resolveRequiredKeys(clone.required, cleanedProps);
    }
  } else if (Array.isArray(clone.required)) {
    delete clone.required;
  }

  if (clone.items !== undefined && clone.items !== null) {
    if (Array.isArray(clone.items)) {
      clone.items = clone.items.map(sanitizeJsonSchemaRequired);
    } else {
      clone.items = sanitizeJsonSchemaRequired(clone.items);
    }
  }

  return clone;
};
